import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image/Image'
import Link from 'UIKit/Link/Link'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text/Text'
import { RouteConfig } from 'config/constants/route'
import { InfoReward } from 'config/types/bonus/WagerAdventure'
import { Game } from 'config/types/game'
import { useCountDown } from 'hooks/useCountdown'
import { useImmutableValueReference } from 'hooks/useImmutableValueReference'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import { usePrivateSocket } from 'hooks/usePrivateSocket'
import { useRouter } from 'hooks/useRouter'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { HunnyToast } from 'utils/toastify'
import { displayCountdownTimeUnit } from 'views/Cashback/utils'
import BonusNameDisplay from 'views/WagerAdventure/components/BonusNameDisplay'
import { getProgressReward, useFetchWagerAdventure, useIsWagerAdventure } from 'views/WagerAdventure/hook'
import {
  ModalWrapper,
  ProgressBar,
  ScaleVariantBonus,
  StyledArrowButton,
  StyledButton,
  StyledTitle,
  Wrapper,
  WrapperContentDropdown,
} from './styled'

const BonusAdventureContent: React.FC<{
  endDate: number
  isMaxReward: boolean
  reward: InfoReward | null
  callback: () => void
  onDismiss: () => void
}> = ({ endDate, reward, isMaxReward, callback, onDismiss }) => {
  const { t } = useTranslation()

  const isExpired = endDate < Date.now()

  const shudownTimeTextRef = useCountDown(
    endDate,
    (s, m, h, d) => `
      ${displayCountdownTimeUnit(t('day'), 'Day', d)}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('hour'), 'Hour', h)}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('min'), 'Min', m)}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('sec'), 'Sec', s)}
    `,
    isExpired ? () => {} : callback,
  )

  return (
    <>
      <Box mx="auto" minWidth={320} minHeight={90} borderRadius={theme.radii.tiny} overflow="hidden">
        <Image src="/images/wager-adventure/adventure-bonus-banner-1.png" width={320} height={90} original />
      </Box>

      {isExpired ? (
        <Text
          small
          textAlign="center"
          color={isExpired ? theme.colors.primary : 'text'}
          letterSpacing="-0.32px"
          mt="20px"
          bold
        >
          <Trans>The campaign has ended</Trans>
        </Text>
      ) : (
        <StyledCountdownContent mt="20px" ref={shudownTimeTextRef} />
      )}

      <Column my="20px">
        {reward && !isMaxReward && (
          <Flex justifyContent="space-between" mb="12px">
            <Text whiteSpace="nowrap" fontSize="12px" color="textSubtle">
              <Trans>Next level reward</Trans>
            </Text>

            <BonusNameDisplay
              ellipsis={2}
              textAlign="right"
              ml="16px"
              fontSize="14px"
              lineHeight={1}
              bold
              reward={reward}
            />
          </Flex>
        )}
        <RowBetween>
          <Text fontSize="12px" color="textSubtle" mr="16px" whiteSpace="nowrap">
            <Trans>How to play</Trans>
          </Text>
          <Text fontSize="12px" textAlign="right" color="textTertiary">
            <Trans>Play any game to increase your level</Trans>
          </Text>
        </RowBetween>
      </Column>

      <Column width="100%">
        <StyledButton
          as={Link}
          href={RouteConfig.WagerAdventure}
          width="100%"
          primary
          fontSize={['14px', '', '12px']}
          onClick={onDismiss}
        >
          <RowCenter
            style={{
              textAlign: 'center',
            }}
            width="100%"
            justifyContent="center !important"
          >
            <Trans>Go to the map</Trans>
          </RowCenter>
        </StyledButton>
      </Column>
    </>
  )
}
export const BonusAdventureDropdown: React.FC<{
  endDate: number
  isMaxReward: boolean
  reward: InfoReward | null
  callback: () => void
}> = ({ endDate, reward, isMaxReward, callback }) => {
  return (
    <WrapperContentDropdown maxWidth="360px !important" p="20px !important" mt="6px !important">
      <BonusAdventureContent
        callback={callback}
        endDate={endDate}
        isMaxReward={isMaxReward}
        reward={reward}
        onDismiss={() => {}}
      />
    </WrapperContentDropdown>
  )
}

export const BonusAdventureModal: React.FC<
  ModalProps<{
    endDate: number
    isMaxReward: boolean
    reward: InfoReward | null
    callback: () => void
  }>
> = ({ onDismiss = () => {}, data: { endDate, isMaxReward, reward, callback } }) => {
  return (
    <ModalWrapper title="Wager Adventure" onDismiss={onDismiss}>
      <BonusAdventureContent
        callback={callback}
        endDate={endDate}
        isMaxReward={isMaxReward}
        reward={reward}
        onDismiss={onDismiss}
      />
    </ModalWrapper>
  )
}

const NotiBadge: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <StyledNotificationContainer href={RouteConfig.BonusCenter}>
      <Text fontSize="12px" lineHeight="20px" fontWeight={400}>
        <Trans>Your reward has been added to the Bonus Center, check it out now!</Trans>
      </Text>
      <StyledCloseIcon
        width="12px"
        height="12px"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onClose()
        }}
      >
        <Icons.ClearIcon fill="#FFF" width="12px" height="12px" />
      </StyledCloseIcon>
    </StyledNotificationContainer>
  )
}

const BonusAdventure: React.FC<{ game: Game } & ScaleVariantBonus & BoxProps> = ({
  game,
  tagButtonEnable,
  ...props
}) => {
  const { isMaxReward, isExpired, wagerAdventure, userWager, progressReward, onUpdateProgress, refetch } =
    useFetchWagerAdventure()
  const [onPresentContent] = useModal(BonusAdventureModal)
  const { t } = useTranslation()

  const { isDesktop } = useMatchBreakpoints()
  const [badge, setBadge] = useState(false)
  const isGameWagerAdventure = useIsWagerAdventure(wagerAdventure?.gameCollectionId, game)

  const socket = usePrivateSocket()

  const router = useRouter()
  const adventure = useImmutableValueReference({
    wagerAdventure,
    userWager,
  })

  const onListenerBet = useCallback(async (data) => {
    const progress = getProgressReward(
      adventure.current.wagerAdventure.rewardByLevels,
      data.level,
      data.wager_amount_usd,
    )
    onUpdateProgress(progress)

    if (data.level > adventure.current.userWager.level) {
      await refetch()
    }
  }, [])

  const onListenerPaid = useCallback(async (data) => {
    if (data.reward_type === 1) {
      const result = await refetch()
      if (result.wagerAdventure.endDate > Date.now()) {
        setBadge(true)
      }
    }
  }, [])

  const onListenerFinalBetWithC2PBonus = useCallback(async () => {
    HunnyToast.warn(
      t('Warning'),
      t(
        'Your bonus balance is not enough to bet. The result of your last bonus bet is still pending. If you continue betting, it will use your real balance.',
      ),
    )
  }, [])

  useEffect(() => {
    if (adventure.current === null || !socket) return

    const betCollectionEvent = socket.on('adventure.bet.collection', onListenerBet)
    const rewardPaidEvent = socket.on('adventure.reward.paid', onListenerPaid)
    const finalBetWithBonusEvent = socket.on('voucher.preloaded.bonus.low', onListenerFinalBetWithC2PBonus)

    return () => {
      betCollectionEvent.off()
      rewardPaidEvent.off()
      finalBetWithBonusEvent.off()
    }
  }, [socket, adventure])

  return (
    wagerAdventure &&
    userWager?.reward?.bonus &&
    !isExpired && (
      <Flex className="wrapper" position="relative" width="100%" height="100%" {...props}>
        <Wrapper
          width="100%"
          dropdownContent={
            isDesktop ? (
              <BonusAdventureDropdown
                reward={userWager.reward}
                isMaxReward={isMaxReward}
                endDate={wagerAdventure?.endDate}
                callback={refetch}
              />
            ) : (
              <></>
            )
          }
          dropdownPosition="bottom-left"
          title={
            <RowBetween width="100%">
              <StyledTitle fontSize={['12px !important', '', '10px !important']}>
                <Trans>Wager Adventure</Trans>
              </StyledTitle>
            </RowBetween>
          }
          onClick={() => {
            if (!isDesktop) {
              onPresentContent({
                endDate: wagerAdventure?.endDate,
                reward: userWager.reward,
                isMaxReward,
                callback: refetch,
              })
            }
          }}
        >
          <RowBetween>
            {isGameWagerAdventure ? (
              <ProgressBar progress={progressReward}>
                <RowMiddle>
                  <Box position="relative" mr="6px" ml="4px" minWidth="20px">
                    <Image src="/images/wager-adventure/star.png" width={20} height={20} />
                    <Text
                      color="#484F5D"
                      fontSize="8px"
                      fontWeight={800}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%,-40%)',
                      }}
                    >
                      {userWager.level}
                    </Text>
                  </Box>

                  <Flex width="100%" justifyContent="space-between">
                    <Box maxWidth={['100%', '', '90%']} mr="12px">
                      <BonusNameDisplay
                        textAlign="left"
                        ellipsis={1}
                        fontSize={['14px', '', '12px']}
                        bold
                        reward={userWager.reward}
                      />
                    </Box>

                    <Text fontSize={['14px', '', '12px']} bold>
                      {progressReward}%
                    </Text>
                  </Flex>
                </RowMiddle>
              </ProgressBar>
            ) : (
              <RowBetween height="100%">
                <Text
                  maxWidth={['100%', '', '80%', '', '', '100%']}
                  style={{
                    wordBreak: 'break-all',
                  }}
                  bold
                  ellipsis={1}
                  fontSize={['14px', '', '12px']}
                  ml="4px"
                >
                  <Trans>Ineligible for wager</Trans>
                </Text>

                <StyledButton
                  height="auto"
                  ml="8px"
                  onClick={() => {
                    router.push({
                      pathname: RouteConfig.Games,
                      query: { gameCollection: wagerAdventure?.gameCollectionId },
                    })
                  }}
                  display={tagButtonEnable ? 'flex !important' : 'none !important'}
                >
                  <Trans>Other Games</Trans>
                </StyledButton>
              </RowBetween>
            )}
            <StyledArrowButton ml="8px" disabled />
          </RowBetween>
        </Wrapper>
        {badge && <NotiBadge onClose={() => setBadge(false)} />}
      </Flex>
    )
  )
}

const StyledCountdownContent = styled(Flex)`
  color: ${({ theme: { colors } }) => colors.textAlt1};
  padding: 8px;
  margin-inline: auto;

  div {
    width: 40px;
    text-align: center;
  }

  span {
    color: ${({ theme: { colors } }) => colors.textAlt1};
    width: 10px;
    text-align: center;
  }

  .day,
  .hour,
  .min,
  .sec {
    .value {
      color: ${({ theme: { colors } }) => colors.text};
      font-weight: bold;
      margin-bottom: 2px;
      font-size: 12px;
    }
    .unit {
      font-size: 10px;
    }
  }
`

const StyledNotificationContainer = styled(Link)`
  display: flex;
  background: ${({ theme }) => theme.colors.modalHeaderBackground} !important;
  border-radius: ${({ theme }) => theme.radii.tiny};
  gap: 8px;
  padding: 4px 8px;
  z-index: 6;
  position: absolute;
  bottom: -30%;
  left: 10%;
  width: max-content;
  transform: translateX(-10%) translateY(100%);
  transition: 0.2s all;
  line-height: 20px;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: -5px;
    left: 40px;
    width: 10px;
    height: 10px;
    transform: rotate(135deg);
    border-radius: 1px;
    background: ${({ theme }) => theme.colors.modalHeaderBackground} !important;
    backdrop-filter: blur(6px);
    z-index: -2;
  }

  display: none;

  ${({ theme }) => theme.mediaQueries.sm} {
    display: flex;
    max-width: 300px;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    display: flex;
    max-width: 400px;
  }
  ${({ theme }) => theme.mediaQueries.xl} {
    display: flex;
    max-width: max-content;
  }
`

const StyledCloseIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: #6c727e;
    fill: #6c727e;
  }
`
export default BonusAdventure
